import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X, GitBranch, RotateCcw, Trash2, User, Clock, FileText, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { TEST_IDS } from "@/testIds";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

/**
 * Sales-bookings version history drawer. Lists past submissions, shows
 * summary counts + totals, and lets you restore any previous version
 * (which creates a new version with note="Restored from vN" so the audit
 * trail is preserved).
 */
export default function SalesVersionsDrawer({ open, onClose, onChanged }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sales-versions");
      setVersions(data);
    } catch (e) {
      toast.error("Failed to load versions", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const activate = async (v) => {
    if (!window.confirm(`Restore v${v.version_no}? Sales bookings will be replaced with this snapshot and a new version marker will be created.`)) return;
    setBusyId(v.id);
    try {
      await api.post(`/sales-versions/${v.id}/activate`);
      toast.success(`Restored to v${v.version_no}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error("Restore failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusyId(null);
    }
  };

  const del = async (v) => {
    if (!window.confirm(`Delete v${v.version_no}? This cannot be undone.`)) return;
    setBusyId(v.id);
    try {
      await api.delete(`/sales-versions/${v.id}`);
      toast.success(`v${v.version_no} deleted`);
      await load();
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusyId(null);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid={TEST_IDS.salesVersionsDrawer}>
      <div className="absolute inset-0 bg-ink/50 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative ml-auto w-full max-w-md h-full bg-paper overflow-y-auto"
        style={{ animation: "cf-sv-in 0.24s cubic-bezier(.22,.61,.36,1) both" }}
      >
        <style>{`@keyframes cf-sv-in { from { transform: translateX(24px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>

        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 bg-paper/95 backdrop-blur border-b border-hairline/60">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-ink flex items-center justify-center">
              <GitBranch size={16} className="text-gold-500" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-light font-semibold">
                Audit trail
              </div>
              <div className="font-display text-lg text-ink" style={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
                Sales Version History
              </div>
            </div>
          </div>
          <button
            data-testid={TEST_IDS.salesVersionsClose}
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-surface flex items-center justify-center text-muted-dark"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {loading && <div className="text-sm text-muted-light">Loading…</div>}
          {!loading && versions.length === 0 && (
            <div className="cf-card p-8 text-center text-sm text-muted-dark">
              <FileText size={20} className="text-muted-light mx-auto mb-2" />
              No versions yet. Click <span className="font-semibold text-ink">Submit as new version</span> to snapshot the current sales data.
            </div>
          )}

          {versions.map((v) => (
            <div
              key={v.id}
              data-testid={TEST_IDS.salesVersionRow(v.version_no)}
              className={`cf-card p-4 space-y-2 ${v.is_active ? "ring-2 ring-gold-500/60" : ""}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">
                      v{v.version_no}
                    </span>
                    {v.is_active && (
                      <span className="cf-pill num" style={{ background: "rgba(251,192,45,0.15)", color: "#8A6800" }}>
                        <CheckCircle2 size={10} /> Active
                      </span>
                    )}
                  </div>
                  {v.note && (
                    <div className="text-sm text-ink font-semibold mt-1 truncate">{v.note}</div>
                  )}
                  <div className="mt-1 flex items-center gap-3 text-[11px] text-muted-dark num flex-wrap">
                    <span className="inline-flex items-center gap-1"><Clock size={10} /> {fmtDateTime(v.submitted_at)}</span>
                    <span className="inline-flex items-center gap-1"><User size={10} /> {v.submitted_by || "system"}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-hairline/40">
                <div className="text-xs text-muted-dark">
                  <span className="num text-ink font-semibold">{v.row_count}</span> row{v.row_count === 1 ? "" : "s"} · total{" "}
                  <span className="num text-ink font-semibold">{formatMoney(v.total_sales_value)}</span>
                </div>
                <div className="flex items-center gap-1">
                  {!v.is_active && (
                    <button
                      data-testid={TEST_IDS.salesVersionActivate(v.version_no)}
                      onClick={() => activate(v)}
                      disabled={busyId === v.id}
                      className="cf-touch inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold text-ink hover:bg-surface transition-colors disabled:opacity-50"
                      title="Restore this version"
                    >
                      <RotateCcw size={11} /> Restore
                    </button>
                  )}
                  {!v.is_active && (
                    <button
                      data-testid={TEST_IDS.salesVersionDelete(v.version_no)}
                      onClick={() => del(v)}
                      disabled={busyId === v.id}
                      className="w-8 h-8 rounded-md hover:bg-surface flex items-center justify-center text-muted-dark hover:text-red-600 transition-colors disabled:opacity-50"
                      aria-label="Delete version"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Modal to capture the note before submitting a new checkpoint version. */
export function SalesSubmitModal({ open, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sales-versions", { note: note.trim() });
      toast.success(`Snapshot saved as v${data.version_no}`, { description: `${data.row_count} rows recorded.` });
      setNote("");
      onDone?.();
    } catch (e) {
      toast.error("Submit failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-ink/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md cf-card p-6 space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-light font-semibold">Sales Team</div>
          <div className="font-display text-xl text-ink mt-1" style={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
            Submit as new version
          </div>
          <p className="text-xs text-muted-dark mt-2">
            Freezes the current Sales Bookings data as an immutable snapshot. Future edits create a new version — no data is lost.
          </p>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-light font-semibold">Note</label>
          <input
            data-testid={TEST_IDS.salesSubmitNoteInput}
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. July 24 team submission"
            className="mt-1 w-full px-3 py-2 bg-surface rounded-lg text-sm text-ink focus:outline-none border border-hairline"
            autoFocus
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid={TEST_IDS.salesSubmitCancel}
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg border border-hairline text-sm text-muted-dark hover:bg-surface-2 transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid={TEST_IDS.salesSubmitConfirm}
            onClick={submit}
            disabled={busy}
            className="flex-1 py-2.5 rounded-lg bg-ink text-white text-sm font-semibold hover:bg-ink/90 transition-colors disabled:opacity-40"
          >
            {busy ? "Saving…" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}
